import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Phone, ShieldCheck, Truck, Wrench } from "lucide-react";
import api, { mediaUrl } from "../lib/api";
import VideoBackground from "../components/VideoBackground";
import FeaturedBuild from "../components/FeaturedBuild";
import { useSite } from "../lib/site";
import { pageHref } from "../lib/format";
import SmartImage from "../components/SmartImage";
import Reviews from "../components/Reviews";
import BlockRenderer from "../components/BlockRenderer";
import Lightbox from "../components/Lightbox";
import SurplusStockBand from "../components/SurplusStockBand";
import useSeo from "../hooks/useSeo";
import { PageLoader } from "../components/States";

const ICONS = [Wrench, Truck, ShieldCheck, Award];

export default function Home() {
  const { site } = useSite();
  useSeo({
    title: "NQ Springs & 4x4 Accessories - Suspension, GVM Upgrades & 4WD Parts, Townsville",
    description:
      "North Queensland's suspension specialists since 1961. Lift kits, GVM upgrades, leaf springs, 4x4 accessories and fitting in Garbutt, Townsville - plus surplus stock at cost price.",
    canonical: "/",
  });
  const [data, setData] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [builds, setBuilds] = useState([]);
  const [lb, setLb] = useState(null);

  useEffect(() => {
    api.get("/pages/home").then((r) => setData(r.data)).catch(() => setData({ page: null }));
    api.get("/builds").then((r) => setBuilds((r.data.builds || []).slice(0, 8))).catch(() => {});
  }, []);

  useEffect(() => {
    const slugs = site?.homepage?.featured_sections || [];
    if (!slugs.length) return;
    Promise.all(
      slugs.map((s) =>
        api
          .get(`/pages/${s}`)
          .then((r) => {
            const p = r.data.page;
            const img =
              p.hero_image ||
              (r.data.children || []).find((c) => c.image)?.image ||
              (p.blocks || []).find((b) => b.type === "image")?.url;
            return {
              slug: s,
              title: p.title,
              image: img,
              count: (r.data.children || []).length,
              excerpt: p.meta_description || "",
            };
          })
          .catch(() => null)
      )
    ).then((rows) => setFeatured(rows.filter(Boolean)));
  }, [site]);

  if (!data) return <PageLoader label="Loading NQ Springs" />;

  const page = data.page || {};
  const stats = site?.stats || [];
  const heroImg =
    page.hero_image || (page.blocks || []).find((b) => b.type === "image")?.url;
  const yt = site?.homepage?.youtube_id;
  const heroVideo = site?.hero_video?.enabled ? site.hero_video.video_id : null;

  return (
    <div data-testid="home-page">
      {/* ---------------- hero ---------------- */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          {heroVideo ? (
            <VideoBackground
              videoId={heroVideo}
              opacity={0.9}
            />
          ) : (
            heroImg && (
              <img src={mediaUrl(heroImg, 1920)} alt="" className="h-full w-full object-cover opacity-40" />
            )
          )}
          <div className={`absolute inset-0 ${heroVideo ? "hero-overlay-video" : "hero-overlay"}`} />
          <div className="noise-overlay" />
        </div>

        <div className="container-nq relative py-20 md:py-28">
          <p className="eyebrow">{site?.branding?.tagline}</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-bold leading-[1.05] sm:text-5xl lg:text-6xl">
            Suspension, GVM upgrades &amp;{" "}
            <span className="gold-text">4x4 accessories</span> built for North Queensland
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[color:var(--nq-stone)] md:text-lg">
            {site?.branding?.description}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/suspension-2" className="btn-gold px-6 py-3 text-sm" data-testid="hero-shop-cta">
              Explore suspension <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/enquire" className="btn-green px-6 py-3 text-sm" data-testid="hero-enquire-cta">
              Get a quote
            </Link>
            <a
              href={`tel:${(site?.contact?.phone || "").replace(/[^\d+]/g, "")}`}
              className="btn-outline-nq px-6 py-3 text-sm"
            >
              <Phone className="h-4 w-4" /> {site?.contact?.phone}
            </a>
          </div>
        </div>
      </section>

      {/* ---------------- featured build ---------------- */}
      <FeaturedBuild build={site?.featured_build} phone={site?.contact?.phone} />

      {/* ---------------- surplus stock at cost price ---------------- */}
      <SurplusStockBand />

      {/* ---------------- stats ---------------- */}
      {stats.length > 0 && (
        <section className="border-y" style={{ borderColor: "rgba(255,255,255,0.08)", background: "var(--nq-ink)" }}>
          <div className="container-nq grid grid-cols-2 gap-6 py-10 md:grid-cols-4">
            {stats.map((s, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <div key={i} className="flex items-start gap-3" data-testid="home-stat">
                  <Icon className="mt-1 h-5 w-5 shrink-0 gold-text" />
                  <div>
                    <p className="text-2xl font-bold leading-none md:text-3xl">{s.value}</p>
                    <p className="mt-1.5 text-xs leading-snug text-[color:var(--nq-stone)]">{s.label}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ---------------- featured categories ---------------- */}
      <section className="section-nq">
        <div className="container-nq">
          <p className="eyebrow">NQ Springs Offers</p>
          <h2 className="mt-2 text-3xl font-bold md:text-4xl">All your 4x4 and trucks needs</h2>
          <p className="mt-3 max-w-2xl text-sm text-[color:var(--nq-stone)] md:text-base">
            Explore our full range across suspension, GVM upgrades, tune packages, trucks,
            American pickups and hundreds of accessories.
          </p>

          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-featured-grid">
            {featured.map((f) => (
              <Link
                key={f.slug}
                to={pageHref(f.slug)}
                className="card-nq group relative flex flex-col"
                data-testid="home-featured-card"
              >
                <SmartImage
                  src={f.image}
                  alt={f.title}
                  ratio="aspect-[16/10]"
                  fit="contain"
                  fallbackLabel={f.title}
                  className="transition-transform duration-500 group-hover:scale-[1.05]"
                />
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="text-lg font-semibold transition group-hover:text-[color:var(--nq-gold)]">
                    {f.title}
                  </h3>
                  {f.excerpt && (
                    <p className="mt-2 line-clamp-2 text-sm text-[color:var(--nq-stone)]">{f.excerpt}</p>
                  )}
                  <span className="mt-auto flex items-center gap-1.5 pt-4 text-xs font-semibold gold-text">
                    {f.count > 0 ? `${f.count} categories` : "View range"}
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- legacy / about ---------------- */}
      <section className="border-y" style={{ borderColor: "rgba(255,255,255,0.08)", background: "var(--nq-ink)" }}>
        <div className="container-nq grid items-center gap-10 py-14 lg:grid-cols-2">
          <div>
            <p className="eyebrow">Our Legacy</p>
            <h2 className="mt-2 text-3xl font-bold md:text-4xl">NQ Springs: A Journey Through Time</h2>
            <div className="mt-5">
              <BlockRenderer
                blocks={(page.blocks || []).filter((b) => b.type === "paragraph").slice(0, 2)}
              />
            </div>
            <Link to="/about-us" className="btn-gold mt-6" data-testid="home-about-cta">
              Learn more <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {yt && (
            <div className="card-nq overflow-hidden">
              <div className="aspect-video w-full">
                <iframe
                  title="NQ Springs 4x4 Accessories"
                  src={`https://www.youtube.com/embed/${yt}`}
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          )}
        </div>
      </section>

      <Reviews />

      {/* ---------------- builds gallery ---------------- */}
      {builds.length > 0 && (
        <section className="section-nq">
          <div className="container-nq">
            <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="eyebrow">Gallery</p>
                <h2 className="mt-2 text-3xl font-bold md:text-4xl">Our Adventure Gallery</h2>
              </div>
              <Link to="/our-builds" className="btn-outline-nq" data-testid="home-builds-cta">
                See all builds <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {builds.map((b, i) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => setLb(i)}
                  className="card-nq cursor-zoom-in"
                  data-testid="home-build-thumb"
                >
                  <SmartImage src={b.image} alt={b.title} ratio="aspect-square" fit="cover" />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------------- CTA band ---------------- */}
      <section className="relative overflow-hidden border-y" style={{ borderColor: "rgba(198,162,74,0.25)" }}>
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(15,61,34,0.5), rgba(11,13,12,0.95))" }} />
        <div className="container-nq relative flex flex-col items-center gap-5 py-14 text-center">
          <h2 className="max-w-2xl text-2xl font-bold md:text-3xl">
            Not sure what suits your vehicle?
          </h2>
          <p className="max-w-xl text-sm text-[color:var(--nq-stone)]">
            Send us your rego and what you&apos;re chasing — our team will match the right setup and
            give you honest advice.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/enquire" className="btn-gold px-6 py-3" data-testid="home-cta-enquire">
              Enquire now
            </Link>
            <Link to="/shop" className="btn-outline-nq px-6 py-3">
              Browse surplus stock
            </Link>
          </div>
        </div>
      </section>

      <Lightbox images={builds.map((b) => b.image)} index={lb} onClose={() => setLb(null)} onIndex={setLb} />
    </div>
  );
}
